import { useState } from "react";
import { MapPin } from "lucide-react";
import { useMunicipality } from "@/context/MunicipalityContext";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { formatMunicipalityLabel } from "@/lib/municipalityLabel";

export const DEFAULT_MUNICIPALITY_SWITCH_DESCRIPTION =
  "This page will show work for the selected municipality. Open items in other tools may still use a different municipality until you change them there.";

type MunicipalitySwitcherProps = {
  confirmDescription?: string;
};

export function MunicipalitySwitcher({
  confirmDescription = DEFAULT_MUNICIPALITY_SWITCH_DESCRIPTION,
}: MunicipalitySwitcherProps) {
  const { municipalities, municipality, isLoading, setMunicipalityId } = useMunicipality();
  const [pendingId, setPendingId] = useState<number | null>(null);

  if (isLoading) {
    return <Skeleton className="h-9 w-64" data-testid="municipality-switcher-loading" />;
  }

  if (municipalities.length === 0) {
    return (
      <p className="text-sm text-muted-foreground" data-testid="municipality-switcher-empty">
        No municipalities assigned to your account.
      </p>
    );
  }

  const label = municipality ? formatMunicipalityLabel(municipality) : "Select municipality";

  if (municipalities.length === 1 && municipality) {
    return (
      <div className="flex items-center gap-2 text-sm font-medium" data-testid="municipality-switcher-single">
        <MapPin className="h-4 w-4 text-muted-foreground" aria-hidden />
        <span>{label}</span>
      </div>
    );
  }

  const requestChange = (nextId: number) => {
    if (municipality?.id === nextId) return;
    if (!municipality?.id) {
      setMunicipalityId(nextId);
      return;
    }
    setPendingId(nextId);
  };

  const confirmChange = () => {
    if (pendingId == null) return;
    setMunicipalityId(pendingId);
    setPendingId(null);
  };

  return (
    <>
      <div className="flex items-center gap-2" data-testid="municipality-switcher">
        <MapPin className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
        <Select
          value={municipality?.id ? String(municipality.id) : undefined}
          onValueChange={(value) => requestChange(Number(value))}
        >
          <SelectTrigger className="w-[min(100%,280px)]" aria-label="Active municipality">
            <SelectValue placeholder="Select municipality">{label}</SelectValue>
          </SelectTrigger>
          <SelectContent>
            {municipalities.map((m) => (
              <SelectItem key={m.id} value={String(m.id)}>
                {formatMunicipalityLabel(m)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <AlertDialog open={pendingId != null} onOpenChange={(open) => !open && setPendingId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Switch municipality?</AlertDialogTitle>
            <AlertDialogDescription>{confirmDescription}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmChange}>Switch</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
